import { EventTimeline, EventType, KnownMembership, Room } from 'matrix-js-sdk/src/matrix';

import { meetsMeetingPermissionThreshold } from 'utils/chatPermissionGates';
import { getUserPowerLevel, isPrivateRoom } from 'utils/matrixUtils';
import { PermissionId, workspacesConfig } from 'utils/powerLevelsConfig';
import { readRoomManagerIds } from 'utils/roomManagers';

export type MeetingPermissionContext = {
  scopedMeetingPl: number;
  inRoomPl: number;
  isRoomManager: boolean;
  isInWorkspace: boolean;
  isRoomOwner: boolean;
};

export const buildMeetingPermissionContext = (
  room: Room,
  actorUserId: string,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean
): MeetingPermissionContext => ({
  scopedMeetingPl,
  inRoomPl: getUserPowerLevel(room, actorUserId),
  isRoomManager: readRoomManagerIds(room)?.has(actorUserId) ?? false,
  isInWorkspace: getIsRoomInCurrentWorkspace(room.roomId),
  isRoomOwner: room.getCreator() === actorUserId,
});

export const meetsMeetingPermissionForContext = (
  ctx: MeetingPermissionContext,
  permissionId:
    | PermissionId.MeetingToggleWaitingList
    | PermissionId.MeetingEditDetails
    | PermissionId.MeetingKickUsers
    | PermissionId.MeetingBanUsers
): boolean => {
  const threshold = workspacesConfig[permissionId].powerLevel;
  if (ctx.isInWorkspace) {
    return meetsMeetingPermissionThreshold(ctx.scopedMeetingPl, ctx.inRoomPl, ctx.isRoomManager, threshold);
  }
  return ctx.isRoomOwner;
};

export const canSendMeetingRoomStateEvent = (
  room: Room,
  actorUserId: string,
  eventType: EventType | string
): boolean => {
  const roomState = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
  return !!roomState?.maySendStateEvent(eventType, actorUserId);
};

const meetsMeetingPermissionForRoom = (
  room: Room | null,
  actorUserId: string | null,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean,
  permissionId:
    | PermissionId.MeetingToggleWaitingList
    | PermissionId.MeetingEditDetails
    | PermissionId.MeetingKickUsers
    | PermissionId.MeetingBanUsers
): boolean => {
  if (!room || !actorUserId) return false;
  if (room.getMyMembership() !== KnownMembership.Join) return false;

  const ctx = buildMeetingPermissionContext(room, actorUserId, scopedMeetingPl, getIsRoomInCurrentWorkspace);
  return meetsMeetingPermissionForContext(ctx, permissionId);
};

export const canToggleWaitingListForRoom = (
  room: Room | null,
  actorUserId: string | null,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean
): boolean =>
  meetsMeetingPermissionForRoom(
    room,
    actorUserId,
    scopedMeetingPl,
    getIsRoomInCurrentWorkspace,
    PermissionId.MeetingToggleWaitingList
  );

export const canEditMeetingDetailsForRoom = (
  room: Room | null,
  actorUserId: string | null,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean
): boolean =>
  meetsMeetingPermissionForRoom(
    room,
    actorUserId,
    scopedMeetingPl,
    getIsRoomInCurrentWorkspace,
    PermissionId.MeetingEditDetails
  );

/** Waiting-list-only edit: non-private, non-scheduled room + PL 25 without PL 30 edit details. */
export const canEditWaitingListOnlyForRoom = (
  room: Room | null,
  isScheduled: boolean,
  actorUserId: string | null,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean
): boolean => {
  if (!room || isScheduled || isPrivateRoom(room)) return false;

  return (
    canToggleWaitingListForRoom(room, actorUserId, scopedMeetingPl, getIsRoomInCurrentWorkspace) &&
    !canEditMeetingDetailsForRoom(room, actorUserId, scopedMeetingPl, getIsRoomInCurrentWorkspace)
  );
};

/** Workspace meeting edit entry: full edit (PL 30) or waiting-list-only on eligible rooms. */
export const canOpenWorkspaceMeetingEditModal = (
  room: Room | null,
  isScheduled: boolean,
  actorUserId: string | null,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean
): boolean =>
  canEditMeetingDetailsForRoom(room, actorUserId, scopedMeetingPl, getIsRoomInCurrentWorkspace) ||
  canEditWaitingListOnlyForRoom(room, isScheduled, actorUserId, scopedMeetingPl, getIsRoomInCurrentWorkspace);

export const canPerformMeetingMembershipAction = (
  room: Room | null,
  actorUserId: string | null,
  action: 'kick' | 'ban'
): boolean => {
  if (!room || !actorUserId) return false;

  const actorPl = getUserPowerLevel(room, actorUserId);
  const roomState = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
  return !!roomState?.hasSufficientPowerLevelFor(action, actorPl);
};

export const canKickFromMeetingForRoom = (
  room: Room | null,
  actorUserId: string | null,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean
): boolean => {
  if (
    !meetsMeetingPermissionForRoom(
      room,
      actorUserId,
      scopedMeetingPl,
      getIsRoomInCurrentWorkspace,
      PermissionId.MeetingKickUsers
    )
  ) {
    return false;
  }

  return canPerformMeetingMembershipAction(room, actorUserId, 'kick');
};

export const canBanFromMeetingForRoom = (
  room: Room | null,
  actorUserId: string | null,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean
): boolean => {
  if (
    !meetsMeetingPermissionForRoom(
      room,
      actorUserId,
      scopedMeetingPl,
      getIsRoomInCurrentWorkspace,
      PermissionId.MeetingBanUsers
    )
  ) {
    return false;
  }

  return canPerformMeetingMembershipAction(room, actorUserId, 'ban');
};

export const canKickMemberFromMeetingRoom = (
  room: Room | null,
  actorUserId: string | null,
  targetUserId: string | null,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean
): boolean => {
  if (!canKickFromMeetingForRoom(room, actorUserId, scopedMeetingPl, getIsRoomInCurrentWorkspace)) return false;
  if (!room || !actorUserId || !targetUserId || actorUserId === targetUserId) return false;

  const actorPl = getUserPowerLevel(room, actorUserId);
  const targetPl = getUserPowerLevel(room, targetUserId);
  return actorPl > targetPl;
};

export const canBanMemberFromMeetingRoom = (
  room: Room | null,
  actorUserId: string | null,
  targetUserId: string | null,
  scopedMeetingPl: number,
  getIsRoomInCurrentWorkspace: (roomId: string) => boolean
): boolean => {
  if (!canBanFromMeetingForRoom(room, actorUserId, scopedMeetingPl, getIsRoomInCurrentWorkspace)) return false;
  if (!room || !actorUserId || !targetUserId || actorUserId === targetUserId) return false;

  const actorPl = getUserPowerLevel(room, actorUserId);
  const targetPl = getUserPowerLevel(room, targetUserId);
  return actorPl > targetPl;
};
